import { Box, ClickAwayListener, MenuItem, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, Col, Dropdown, Row } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import * as XLSX from "xlsx"
import { forceTextOnDateCellsXlsx } from 'utils/excel.utils'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi, { BranchInterface } from 'api/master/clinic.api'
import ReportApi from 'api/report.api'
import { getBranch, toBuddhistYear } from 'utils/app.utils'
import { dateTimeToView } from 'utils/date.utils'

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import FilterSelect from 'component/Select/FilterSelect'
import BalanceList from './BalanceList'

/**STYLE */
import ComponentApi from 'api/components-api'
import ButtonCustom from 'component/Button/ButtonCustom'
import InputCheckbox from 'component/Input/InputCheckbox'
import GoogleExcelSelectSearch from 'component/Search/GoogleExcelSelectSearch'
import SearchPatients from 'component/SearchPatients'
import AutocompleteSelect from 'component/Select/AutocompleteSelect'
import { colors } from 'constants/theme'
import { CustomFilterDoctor, ListFilterDoctors } from 'features/counter/appointment/head-appointment'
import * as UseStyled from 'features/report/useStyled'
import { SpanClearFilter } from 'features/report/useStyled'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

export default function Balance() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const momentNowRange = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]

  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [operativeCode, setOperativeCode] = useState<any[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createdAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [doctors, setDoctors] = useState<Doctors[]>([])
  const [searchTrigger, setSearchTrigger] = useState(0) // ใช้เป็น trigger สำหรับค้นหา
  const [fetch, setFetch] = useState(false)
  const [rows, setRows] = useState<any[]>([])
  const [rowsExport, setRowsExport] = useState<any[]>([])
  const [rangeDate, setRangeDate] = useState<string[]>(momentNowRange)
  const [page, setPage] = useState<number>(1)
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [loading, setLoading] = useState<boolean>(true)
  const [exporting, setExporting] = useState<boolean>(false)
  const [renderExporting, setRenderExporting] = useState<boolean>(false)
  const [readyToExport, setReadyToExport] = useState<boolean>(false)
  const [selectDoctors, setSelectDoctors] = useState<any[]>([])
  // const [codeForm, setCodeForm] = useState<{ operativeCode: string }>({ operativeCode: '' })
  const [codeForm, setCodeForm]: any = useState(null)

  const [codeTo, setCodeTo] = useState<{ operativeCode: string }>({ operativeCode: '' })
  const [billStatus, setBillStatus] = useState<string>('ALL')
  const [patientId, setPatientId] = useState<number>(0)
  const [patientFilter, setPatientFilter] = useState<string>(t('REPORT.REPORT_GROUP.ALL'))

  const [doctorsMultiple, setDoctorsMultiple] = useState<DentistInterface[]>([])
  const [openFilterDoctors, setOpenFilterDoctors] = useState(false)

  const handleFilterDoctors = (value: any) => {
    if ((value === 'ALL' && _.size(selectDoctors.filter((item) => typeof item === 'number')) === _.size(doctors)) || value === 'CLEAR') setSelectDoctors([''])
    else if (value === 'ALL') setSelectDoctors(['ALL', ..._.map(doctors, (d: any) => d.userId)])
    else if (_.includes(selectDoctors, value)) setSelectDoctors(_.filter(selectDoctors, (d) => d !== value && d !== 'ALL'))
    else setSelectDoctors([...selectDoctors, value])
  }

  const getFilterUser = useCallback(() => {
    if (_.includes(selectDoctors, 'ALL')) return ` ${t('REPORT.REPORT_GROUP.ALL')}`
    else {
      return ` ${doctorsMultiple
        .filter((d: DentistInterface) => selectDoctors.includes(d.dentistId))
        ?.map((d: DentistInterface) => d.dentistFullname)
        .join(', ')}`
    }
  }, [selectDoctors, doctorsMultiple, t])

  const verifyPermission = async () => {
    try {
      const [branchesRes, itemCodeRes] = await Promise.all([
        ClinicApi.findAllBranches(),
        ComponentApi.findAllItemCode(),
      ])

      setBranches(branchesRes.data)
      setOperativeCode(itemCodeRes.data)

    } catch (e) {
      console.error('Error loading initial data:', e)
    }
  }

  const loadDoctor = async (branchId: number) => {
    setLoading(true)
    try {
      const doctorsRes = await ClinicApi.findAllDoctors('', '', rangeDate[0], rangeDate[1], branchId)

      setDoctors([...doctorsRes.data])
      setSelectDoctors(['ALL', ...doctorsRes.data.map((d: any) => d.userId)])
      setFetch(true)
    } catch (e) {
      console.error('Error loading initial data:', e)
      setLoading(false)
    }
  }

  useEffect(() => {
    verifyPermission()
  }, [])
  useEffect(() => {
    loadDoctor(branchId)
  }, [branchId])

  const fetchData = useCallback(async () => {
    setLoading(true)
    let condition: any = {
      page: page,
      pageLimit: pageLimit,
      branchId: branchId,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1],
      code: codeForm && codeForm.length ? _.map(codeForm, 'value').join(',') : '',
      billStatus: billStatus === 'ALL' ? '' : billStatus,
      // ไม่ใส่ export: 1 เพื่อให้ได้ pagination
    }
    if (selectDoctors.length) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (selectDoctors[0] !== "ALL" && doctor) condition = { ...condition, doctorId: doctor }
    }
    if (patientId) condition = { ...condition, patientId: patientId }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    try {
      const { headers, data } = await ReportApi.reportBalance(condition)
      setRows(data.rows || [])
      setRowCount(Number(headers['x-total']) || 0)
    } catch (e) {
      console.error('Error fetching balance data:', e)
      setRows([])
      setRowCount(0)
    } finally {
      setLoading(false)
      setFetch(false)
    }
  }, [page, pageLimit, branchId, rangeDate, codeForm, billStatus, selectDoctors, patientId, sortBy, sortType, fetch])

  // ดึงข้อมูลสำหรับ export (จำกัด 1000 รายการเพื่อป้องกัน memory issue)
  const fetchDataForExport = useCallback(async () => {
    let condition: any = {
      page: 1,
      pageLimit: 1000, // จำกัดจำนวนเพื่อป้องกัน memory issue
      branchId: branchId,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1],
      code: codeForm && codeForm.length ? _.map(codeForm, 'value').join(',') : '',
      billStatus: billStatus === 'ALL' ? '' : billStatus,
      export: 1
    }
    if (selectDoctors.length) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (selectDoctors[0] !== "ALL" && doctor) condition = { ...condition, doctorId: doctor }
    }
    if (patientId) condition = { ...condition, patientId: patientId }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    try {
      const { data } = await ReportApi.reportBalance(condition)
      setRowsExport(data.rows || [])
      return true
    } catch (e) {
      console.error('Error fetching export data:', e)
      return false
    }
  }, [branchId, rangeDate, codeForm, billStatus, selectDoctors, patientId, sortBy, sortType])

  const exportTableToExcel = async () => {
    // แสดง exporting loading
    setExporting(true)

    try {
      // ดึงข้อมูลทั้งหมดก่อน export
      const success = await fetchDataForExport()

      if (!success) {
        setExporting(false)
        alert('ไม่สามารถดึงข้อมูลได้ กรุณาลองอีกครั้ง')
        return
      }

      // ตั้ง flag ให้ useEffect ทำการ export หลังจาก component render
      setRenderExporting(true)
      setReadyToExport(true)
    } catch (error) {
      console.error('Error preparing export:', error)
      setExporting(false)
      setRenderExporting(false)

      alert('เกิดข้อผิดพลาดในการเตรียมข้อมูล กรุณาลองอีกครั้ง')
    }
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
    setPage(1) // Reset หน้าเป็นหน้า 1 เมื่อ sort
    setFetch(true)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
    setLoading(true)
    setFetch(true)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
    setLoading(true)
    setFetch(true)
  }

  // เรียก API เฉพาะเมื่อ: กดค้นหา, เปลี่ยนหน้า, เปลี่ยน pageLimit, หรือ sort
  useEffect(() => {
    if (!fetch) return
    fetchData()
  }, [searchTrigger, page, pageLimit, sortBy, sortType, fetch])


  const handleClear = () => setPatientId(0)

  // Memoize filter object เพื่อป้องกันการสร้าง object ใหม่ทุกครั้ง
  const filterMemo = useMemo(() => ({
    branch: `${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn')}`,
    rangeDate: rangeDate,
    doctor: getFilterUser(),
    patient: patientFilter,
    billStatus: billStatus === 'ALL' ? t(`REPORT.FILTER.ALL`) : t(`REPORT.FILTER.BILL.${billStatus}`),
    code: codeForm && codeForm.length ? _.map(codeForm, 'value').join(',') : '',
  }), [branches, branchId, rangeDate, getFilterUser, patientFilter, billStatus, codeForm, t])

  const getAllFilter = () => {
    return [
      {
        label: t('REPORT.FILTER.BRANCH'),
        value: filterMemo.branch
      },
      {
        label: 'วันที่เริ่ม',
        value: toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY')
      },
      {
        label: 'วันที่สิ้นสุด',
        value: toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY')
      },
      {
        label: 'ผู้ป่วย',
        value: patientFilter
      },
      {
        label: 'Code',
        value: filterMemo.code || '',
      },
      {
        label: t('REPORT.FILTER.DOCTOR'),
        value: getFilterUser()
      },
      {
        label: 'สถานะใบเสร็จ',
        value: filterMemo.billStatus
      }
    ]
  }

  // ใช้ข้อมูลสำหรับ export (ถ้ามี rowsExport ใช้ rowsExport ไม่งั้นใช้ rows)
  const dataForExport = useMemo(() => {
    return rowsExport.length > 0 ? rowsExport : rows
  }, [rowsExport, rows])

  // Export เมื่อข้อมูลพร้อม - เขียนใหม่เพื่อประสิทธิภาพสูงและได้ผลลัพธ์ตาม print-show
  useEffect(() => {
    if (readyToExport) {
      const timer = setTimeout(() => {
        try {
          const allRows: any[][] = []

          // 1. เพิ่มข้อมูล filter ด้านบน
          allRows.push([t(`REPORT.REPORT_NAME.BALANCE`)])
          allRows.push([
            t("REPORT.FILTER.BRANCH"), filterMemo.branch,
            t('REPORT.FILTER.DATE_START'), filterMemo.rangeDate[0],
            t('REPORT.FILTER.DATE_END'), filterMemo.rangeDate[1],
            t('REPORT.FILTER.DOCTOR'), filterMemo.doctor
          ])
          allRows.push([
            t("REPORT.FILTER.PATIENT"), filterMemo.patient,
            t('REPORT.FILTER.RECEIPT_STATUS'), filterMemo.billStatus,
            t('Code'), filterMemo.code || '-'
          ])
          allRows.push(['']) // บรรทัดว่าง

          // 2. เพิ่ม Headers
          const headers = [
            t('REPORT.TABLE.DF.#NO'),
            'วันที่',
            'เลขที่ใบเสร็จ',
            'สถานะใบเสร็จ',
            'รหัสผู้ป่วย',
            'รหัสผู้ป่วยในสาขา',
            'ชื่อผู้ป่วย',
            'ทันตแพทย์',
            'Code',
            'รายการ',
            'ราคาต่อหน่วย',
            'จำนวน',
            'ราคาค่ารักษา',
            'ยอดรวมสุทธิ DF',
            'ยอดรวมสุทธิ CF',
            'ยอดรวมสุทธิค่ารักษา',
            'ค้างชำระ DF',
            'ค้างชำระ CF',
            'Balance',
            'ผู้บันทึก',
            'รายละเอียด',
            'หมายเหตุการยกเลิก',
            'ผู้ยกเลิก',
          ]
          allRows.push(headers)

          // 3. เพิ่มข้อมูลแต่ละแถว (ประมวลผลแบบ chunk)
          const CHUNK_SIZE = 5000
          for (let i = 0; i < rowsExport.length; i += CHUNK_SIZE) {
            const chunk = rowsExport.slice(i, i + CHUNK_SIZE)
            chunk.forEach((balance: any, chunkIndex: number) => {
              const rowIndex = i + chunkIndex
              const code = balance.code ? balance.code : '-'
              const remark = balance.remark ? balance.remark.replace('<br/>', '\n') : ''

              allRows.push([
                rowIndex + 1,
                dateTimeToView(balance.createdAt),
                balance.paymentNumber || '',
                t(`REPORT.BILL_STATUS.${balance.statusOrder}`) || '',
                balance.cnNumber || '',
                balance.branchCnNumber || '',
                balance.patientFullName || '',
                balance.doctorFullName || '',
                code,
                balance.itemName || '',
                balance.price || 0,
                balance.qty || 0,
                balance.totalOriginal || 0,
                balance.dfBahtOriginal || 0,
                balance.cfBahtOriginal || 0,
                balance.total || 0,
                balance.overdueDf || 0,
                balance.overdueCf || 0,
                (Number(balance.overdueDf || 0) + Number(balance.overdueCf || 0)),
                balance.staff || '',
                remark,
                balance.cancelRemark || '',
                balance.cancelBy || '',
              ])
            })
          }

          // 4. เพิ่มแถว Sum
          const sumRow: any[] = [
            '', '', '', '', '', '', '', '', '',
            t('REPORT.TITLE.SUM'),
            _.sumBy(rowsExport, 'price') || 0,
            _.sumBy(rowsExport, 'qty') || 0,
            _.sumBy(rowsExport, 'totalOriginal') || 0,
            _.sumBy(rowsExport, 'dfBahtOriginal') || 0,
            _.sumBy(rowsExport, 'cfBahtOriginal') || 0,
            _.sumBy(rowsExport, 'total') || 0,
            _.sumBy(rowsExport, 'overdueDf') || 0,
            _.sumBy(rowsExport, 'overdueCf') || 0,
            _.sumBy(rowsExport, (o: any) => Number(o.overdueDf || 0) + Number(o.overdueCf || 0)),
            '', '', '', ''
          ]
          allRows.push(sumRow)

          // 5. สร้าง worksheet
          const worksheet = XLSX.utils.aoa_to_sheet(allRows)

          // 6. Format code column เป็น text
          const codeColIndex = 8 // Column I (0-indexed, เพราะมี filter rows ด้านบน)
          const headerRowOffset = 5
          for (let i = 0; i < rowsExport.length; i++) {
            const cellRef = XLSX.utils.encode_cell({ r: i + headerRowOffset, c: codeColIndex })
            if (worksheet[cellRef]) {
              worksheet[cellRef].z = '@' // Format as text
            }
          }

          // ระบุ Index ของคอลัมน์ที่เป็นตัวเลขทั้งหมด (เริ่มนับจาก 0)
          const numberColumnIndices = [
            10, 11, 12, 13, 14, 15, 16, 17, 18
          ]

          const totalDataRows = rowsExport.length;
          const sumRowIndex = headerRowOffset + totalDataRows; // แถวของ Sum

          // วนลูปทุกแถวข้อมูล + แถว Sum
          for (let r = headerRowOffset; r <= sumRowIndex; r++) {
            for (const c of numberColumnIndices) {
              const cellRef = XLSX.utils.encode_cell({ r: r, c: c })
              if (worksheet[cellRef]) {
                worksheet[cellRef].t = 'n' // 'n' = Number Type
              }
            }
          }

          // 7. Export
          forceTextOnDateCellsXlsx(worksheet)
          const workbook = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(workbook, worksheet, t(`REPORT.REPORT_NAME.BALANCE`))
          XLSX.writeFile(workbook, `${t(`REPORT.REPORT_NAME.BALANCE`)}.xlsx`)
        } catch (error) {
          console.error('Error exporting to Excel:', error)
          alert('เกิดข้อผิดพลาดในการ export ข้อมูล กรุณาลองอีกครั้ง')
        } finally {
          setRenderExporting(false)
          setExporting(false)
          setReadyToExport(false)
        }
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [readyToExport, rowsExport, t, filterMemo])

  return (
    <>
      <Card className={'border-0 h-100'}>
        <HeaderCard text={t(`REPORT.REPORT_NAME.BALANCE`)} />
        <Card.Body>
          <Card.Title className={'mb-0'}>
            <Row className="mx-0">
              <Col sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                <FilterSelect
                  onchange={(e) => {
                    setBranchId(e.target.value)
                    setPage(1)
                  }}
                  renderValue={() => `${t('REPORT.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId: branchId }), 'branchName') || _.get(_.find(branches, { branchId: branchId }), 'branchNameEn') || ''} `}
                  label={''}
                  selectId="select-branch"
                  labelId="label-branch"
                  value={branchId}
                  options={_.map(branches, (b: BranchInterface, index) => {
                    return (
                      <MenuItem key={b.branchId} value={b.branchId}>
                        {b.branchName || b.branchNameEn}
                      </MenuItem>
                    )
                  })}
                  formControlStyle={{ maxWidth: 'unset !important' }}
                  classesOption="style-select-doctors"
                />
              </Col>
              <Col sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                <InputSecondNewRangePicker
                  inputHeight={32}
                  value={rangeDate || emptyRangePicker}
                  onchange={(val: any) => {
                    if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                    else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                  }}
                  label={''}
                />
              </Col>
              <Col sm={6} md={4} xl={4} className={'pt-2 px-1'}>
                <SearchPatients
                  status="ACTIVE"
                  callbackPatientId={setPatientId}
                  patientId={patientId}
                  onClear={handleClear}
                  callbackPatientName={(value) => {
                    setPatientFilter(value)
                  }}
                />
              </Col>
              <Col sm={6} md={4} xl={3} className="pl-md-0 pt-2">
                <GoogleExcelSelectSearch
                  labelId="code-multi-select"
                  inputLabel="Code"
                  data={[..._.map(_.uniq(operativeCode), (item) => ({ id: item.productCode, label: item.productCode, value: item.productCode }))] as any}
                  columns={['value']}
                  multiSelect={true}
                  height={40}
                  disableClearable={true}
                  selectedRows={codeForm || []}
                  onSelectionChange={(rows: any) => setCodeForm(rows)}
                  onClearAll={() => setCodeForm([])}
                  onSelectAll={() => setCodeForm([..._.map(_.uniq(operativeCode), (item) => ({ id: item.productCode, label: item.productCode, value: item.productCode }))] as any)}
                  limitTags={1}
                  maxResults={_.uniq(operativeCode).length + 1}
                />
              </Col>
              {/* <Col sm={6} md={4} xl={3} className="pl-md-0 pt-2">
                <Box className="position-relative" sx={{ '.MuiOutlinedInput-input': { paddingLeft: '70px !important' } }}>
                  <Typography className="position-absolute top-50 translate-middle-y" sx={{ left: '12px', zIndex: 1 }}>
                    {t('Code To')}:
                  </Typography>
                  <AutocompleteSelect
                    labelId="code-to"
                    noOptionsText={t('NOT_FOUND')}
                    options={operativeCode}
                    getOptionLabel={(option: any) => option?.operativeCode || ''}
                    renderOption={(props, option: any) => (
                      <Box key={option} component="li" {...props}>
                        {option.operativeCode}
                      </Box>
                    )}
                    onchange={(e, value: any) => {
                      setPage(1)
                      setCodeTo(value)
                    }}
                    value={codeTo}
                    height={32}
                    disabledBorder
                  />
                </Box>
              </Col> */}
              <Col xs={'12'} sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                <ClickAwayListener onClickAway={() => setOpenFilterDoctors(false)}>
                  <CustomFilterDoctor open={openFilterDoctors} onClick={() => setOpenFilterDoctors(true)}>
                    <AutocompleteSelect
                      open={openFilterDoctors}
                      multiple
                      labelId="label-doctors"
                      options={['ALL', ...doctors, 'CLEAR']}
                      getOptionLabel={(option) => _.find(doctors, { userId: option.userId })?.fullname || '-'}
                      renderOption={(props, option) => {
                        if (option === 'ALL') {
                          return (
                            <ListFilterDoctors {...props} key={option} component="li" value={option}>
                              <InputCheckbox
                                label={t('REPORT.REPORT_GROUP.ALL')}
                                checked={_.size(selectDoctors.filter((item) => typeof item === 'number')) === _.size(doctors)}
                                indeterminate={_.size(selectDoctors) > 1 && _.size(selectDoctors.filter((item) => typeof item === 'number')) !== _.size(doctors)}
                                className={'pe-none'}
                              />
                            </ListFilterDoctors>
                          )
                        } else if (option === 'CLEAR') {
                          return (
                            <Box {...props} key={option} component="li" value={option} sx={{ borderTop: `1px solid ${colors.lightGray}` }}>
                              <SpanClearFilter className="pe-none">{t('REPORT.FILTER.CLEAR_FILTER')}</SpanClearFilter>
                            </Box>
                          )
                        } else {
                          return (
                            <ListFilterDoctors {...props} key={option.userId} component="li" value={option.userId}>
                              <InputCheckbox label={option.fullname} checked={_.includes(selectDoctors, option.userId)} className={'pe-none'} />
                            </ListFilterDoctors>
                          )
                        }
                      }}
                      renderTags={(tagValue, getTagProps) => {
                        return (
                          <>
                            <span className="pr-1">{t('USER_DOCTOR')}:</span>
                            {!openFilterDoctors &&
                              ((_.size(tagValue.filter((item: any) => typeof item === 'number')) === _.size(doctors) && <span>{t('REPORT.REPORT_GROUP.ALL')}</span>) || (
                                <Box className="text-ellipsis">
                                  {_.map(tagValue, (option: any, index: number) => {
                                    return (
                                      <span>
                                        {_.find(doctors, { userId: option })?.fullname}
                                        {index !== _.findLastIndex(tagValue) && option && `,`}
                                      </span>
                                    )
                                  })}
                                </Box>
                              ))}
                          </>
                        )
                      }}
                      onchange={(e, value) => handleFilterDoctors(e?.target?.value || e?.target?.attributes?.value?.value)}
                      noOptionsText={t('NOT_FOUND')}
                      filterOptions={(option) => 'ALL' + option.fullname + 'CLEAR'}
                      value={selectDoctors}
                      height={32}
                      disableClearable
                    />
                  </CustomFilterDoctor>
                </ClickAwayListener>
              </Col>
              <Col sm={6} md={4} xl={3} className={'pt-2 px-1'}>
                <FilterSelect
                  onchange={(e) => {
                    setBillStatus(e.target.value)
                    setPage(1)
                  }}
                  renderValue={() => `${t('REPORT.FILTER.RECEIPT_STATUS')}: ${billStatus === 'ALL' ? t(`REPORT.FILTER.ALL`) : t(`REPORT.FILTER.BILL.${billStatus}`)} `}
                  label={''}
                  selectId="select-bill-status"
                  labelId="label-bill-status"
                  value={billStatus}
                  options={[
                    <MenuItem key="1" value="ALL">
                      {t('REPORT.FILTER.ALL')}
                    </MenuItem>,
                    <MenuItem key="2" value="NORMAL">
                      {t('REPORT.FILTER.BILL.NORMAL')}
                    </MenuItem>,
                    <MenuItem key="3" value="CANCEL">
                      {t('REPORT.FILTER.BILL.CANCEL')}
                    </MenuItem>
                  ]}
                  formControlStyle={{ maxWidth: 'unset !important' }}
                  classesOption="style-select-doctors"
                />
              </Col>
              <Col md={1} xl={1} className={'pt-0'}>
                <ButtonCustom
                  onClick={() => {
                    setFetch(true)
                    setPage(1) // Reset หน้าเป็นหน้า 1
                    setSearchTrigger(prev => prev + 1) // เพิ่ม trigger เพื่อเรียก API
                  }}
                  textButton={t('INPUT.SEARCH')}
                  style={{ height: '30px', marginTop: '5px' }}
                />
              </Col>

              <Col md={4} xl={2} className={'d-xl-flex justify-content-xl-end ml-xl-auto'}>
                <UseStyled.DropdownDocument>
                  <Dropdown.Toggle id="dropdown-basic" className="ml-xl-auto">
                    {t('REGISTER.BUTTON.PRINT_DOCUMENT')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={exportTableToExcel}>{t('Excel')}</Dropdown.Item>
                  </Dropdown.Menu>
                </UseStyled.DropdownDocument>
              </Col>
            </Row>
          </Card.Title>
          <Box className={'mt-3 pl-xl-3 pr-xl-2 pb-4'}>
            <BalanceList
              filterTable={getAllFilter()}
              page={page}
              pageLimit={pageLimit}
              rowCount={rowCount}
              sortBy={sortBy}
              sortType={sortType}
              data={rows}
              dataExport={rowsExport}
              onRequestSort={onRequestSort}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleChangePage={handleChangePage}
              setSortType={setSortType}
              setSortBy={setSortBy}
              componentRef={componentRef}
            />
          </Box>
        </Card.Body>
        <Loading show={loading} type="fullLoading" />
        {exporting && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
          >
            <Box sx={{ backgroundColor: 'white', padding: 3, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h6">{t('กำลังเตรียมข้อมูลสำหรับ Export...')}</Typography>
            </Box>
          </Box>
        )}
      </Card>
    </>
  )
}
